import React from "react";
import { Circle, Group, Rect, Text } from "react-konva";
import { RESERVATION_CONSTANTS } from "src/config/constants/reservation-constants";

const FloorPlanTable = ({ table, mode, isEditing = true, isManagementSection = true, status, timeLabel, shapeRefs, isSelected = false, onSelect, onDragEnd, onTransformEnd }) => {
    const isSingle = table.capacity === 1;
    const commonProps = {
        key: table.id,
        draggable: mode === 'select' && isEditing,
        border: "1px solid #007AAF",
        x: table.x,
        y: table.y,
        rotation: table.rotation,
        onClick: (e) => onSelect(e, table.id),
        onDragEnd: (e) => onDragEnd(e, table.id),
        onTransformEnd: (e) => onTransformEnd(e, table.id),
        ref: (node) => {
            if (shapeRefs?.current) {
                shapeRefs.current[table.id] = node;
            }
        },
    };

    const widthBasedOnCapacity = (capacity) => {
        if (capacity <= 4) return 50;
        if (capacity <= 6) return 88;
        if (capacity <= 8) return 126;
        return 164;
    };

    const secondaryFillBasedOnStatus = () => {
        switch (status) {
            case RESERVATION_CONSTANTS.RESERVATION_STATUS.CONFIRMED: return '#34C759';
            case RESERVATION_CONSTANTS.RESERVATION_STATUS.PENDING: return '#FF9500';
            case RESERVATION_CONSTANTS.RESERVATION_STATUS.WAITING_FOR_PAYMENT: return '#007AFF';
            case RESERVATION_CONSTANTS.RESERVATION_STATUS.BLOCKED: return '#FF3B30';
            case RESERVATION_CONSTANTS.RESERVATION_STATUS.WAITLIST: return '#00B8D9';
            default: return "#666";
        }
    }

    const fillBasedOnStatus = () => {
        switch (status) {
            case RESERVATION_CONSTANTS.RESERVATION_STATUS.CONFIRMED: return '#2A9F47';
            case RESERVATION_CONSTANTS.RESERVATION_STATUS.PENDING: return '#CC7700';
            case RESERVATION_CONSTANTS.RESERVATION_STATUS.WAITING_FOR_PAYMENT: return '#0062CC';
            case RESERVATION_CONSTANTS.RESERVATION_STATUS.BLOCKED: return '#CC2F26';
            case RESERVATION_CONSTANTS.RESERVATION_STATUS.WAITLIST: return '#0099B3';
            default: return "#454545";
        }
    }

    const width = widthBasedOnCapacity(table.capacity);
    const selectionX = table.capacity === 2 ? -1 : -7;
    const selectionWidth = table.capacity === 2 ? width + 2 : table.capacity % 2 === 0 ? width + 14 : width + 7;

    const chipColor = status ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.1)";
    const chipTextColor = "white";
    const chipHeight = 16;
    const chipRadius = 4;

    const renderTableChair = (x, y, position) => {
        return (
            <Rect
                x={x}
                y={y}
                fill={secondaryFillBasedOnStatus()}
                cornerRadius={position === "top" ? [8, 8, 0, 0] : position === "bottom" ? [0, 0, 8, 8] : position === "left" ? [8, 0, 0, 8] : [0, 8, 8, 0]}
                width={position === "top" || position === "bottom" ? 26 : 6}
                height={position === "top" || position === "bottom" ? 6 : 26}
            />
        )
    }

    if (!isManagementSection) {
        return (
            <Group {...commonProps}>
                <Circle x={0} y={0} radius={25} fill={"rgba(167, 167, 167, 0.2)"} />
                <Text
                    text={`${table.name} (${table.capacity})`}
                    fontSize={12}
                    fill="white"
                    align="center"
                    verticalAlign="middle"
                    width={50}
                    x={-25}
                    y={-6}
                />
            </Group>
        )
    }



    if (isSingle) {
        return (
            <Group {...commonProps}>
                {isSelected && <Rect x={-1} y={-1} width={46} height={38} stroke="#007AFF" cornerRadius={4} />}
                <Rect x={0} y={0} width={44} height={36} cornerRadius={[24, 24, 8, 8]} fill={secondaryFillBasedOnStatus()} />
                <Rect x={8} y={8} width={28} height={28} fill={fillBasedOnStatus()} cornerRadius={[24, 24, 0, 0]} />
                <Text text={table.name} fontSize={12} fill="white" width={44} align="center" y={18} />
                {timeLabel && (
                    <>
                        <Rect
                            x={1}
                            y={42}
                            width={42}
                            height={chipHeight}
                            fill={chipColor}
                            stroke="rgba(255,255,255,0.3)"
                            strokeWidth={1}
                            cornerRadius={chipRadius}
                        />
                        <Text
                            x={1}
                            y={45}
                            width={42}
                            height={chipHeight}
                            text={timeLabel}
                            fontSize={10}
                            fill={chipTextColor}
                            align="center"
                        />
                    </>
                )}
            </Group>
        );
    }



    return (
        <Group {...commonProps}>
            {isSelected && <Rect y={-7} x={selectionX} width={selectionWidth} height={64} stroke="#007AFF" cornerRadius={4} />}
            <Rect width={width} height={50} fill={fillBasedOnStatus()} cornerRadius={8} />
            <Text y={20} text={`${table.name} (${table.capacity})`} fontSize={12} fill="white" align="center" width={width} />
            {timeLabel && (
                <>
                    <Rect
                        x={width / 2 - 22}
                        y={60}
                        width={44}
                        height={chipHeight}
                        fill={chipColor}
                        stroke="rgba(255,255,255,0.3)"
                        strokeWidth={1}
                        cornerRadius={chipRadius}
                    />
                    <Text
                        x={width / 2 - 22}
                        y={63}
                        width={44}
                        height={chipHeight}
                        text={timeLabel}
                        fontSize={10}
                        fill={chipTextColor}
                        align="center"
                    />
                </>
            )}
            {renderTableChair(12, -6, "top")}
            {renderTableChair(12, 50, "bottom")}
            {table.capacity > 2 && renderTableChair(-6, 12, "left")}
            {table.capacity > 3 && table.capacity % 2 === 0 && renderTableChair(50 + (table.capacity / 2 - 2) * 38, 12, "right")}
            {table.capacity > 4 && renderTableChair(50, -6, "top")}
            {table.capacity > 4 && renderTableChair(50, 50, "bottom")}
            {table.capacity > 6 && renderTableChair(88, -6, "top")}
            {table.capacity > 6 && renderTableChair(88, 50, "bottom")}
            {table.capacity > 8 && renderTableChair(126, -6, "top")}
            {table.capacity > 8 && renderTableChair(126, 50, "bottom")}
        </Group>
    );
};

export default React.memo(FloorPlanTable);;
